import { convertToMono, type MonoConversion } from "./mono-image";
import {
  TACTIC_LOCKUP_PATHS,
  TACTIC_LOCKUP_VIEWBOX
} from "./standard/tactic-lockup";

/**
 * Canvas drawing primitives shared by badge themes. Everything takes logical
 * px (the engine already applied the landscape rotation) and a colour string.
 */

export const DISPLAY_FONT_FAMILY = '"Mona Sans", system-ui, sans-serif';
export const BODY_FONT_FAMILY = "Inter, system-ui, sans-serif";

export type HorizontalAlign = "left" | "center";

export const fontString = (
  weight: number,
  size: number,
  family: string
): string => `${weight} ${size}px ${family}`;

/** Measure callback for the pure name-layout helper, bound to one font family/weight. */
export const measureWith =
  (ctx: CanvasRenderingContext2D, weight: number, family: string) =>
  (text: string, fontSize: number): number => {
    ctx.font = fontString(weight, fontSize, family);
    return ctx.measureText(text).width;
  };

/** Rasterise `image` into an offscreen canvas of the given size, centre-cropped ("cover"). */
const rasteriseCover = (
  image: HTMLImageElement,
  width: number,
  height: number,
  mono: MonoConversion | null
): HTMLCanvasElement | null => {
  const offscreen = document.createElement("canvas");
  offscreen.width = Math.max(1, Math.round(width));
  offscreen.height = Math.max(1, Math.round(height));
  const ctx = offscreen.getContext("2d");
  if (!ctx) return null;

  const scale = Math.max(
    offscreen.width / image.naturalWidth,
    offscreen.height / image.naturalHeight
  );
  const drawW = image.naturalWidth * scale;
  const drawH = image.naturalHeight * scale;
  ctx.drawImage(
    image,
    (offscreen.width - drawW) / 2,
    (offscreen.height - drawH) / 2,
    drawW,
    drawH
  );

  if (mono) {
    const pixels = ctx.getImageData(0, 0, offscreen.width, offscreen.height);
    convertToMono(pixels.data, pixels.width, pixels.height, mono);
    ctx.putImageData(pixels, 0, 0);
  }
  return offscreen;
};

export interface CircularImageOptions {
  /** Pre-convert to one-bit for thermal printers; null keeps the colours. */
  mono: MonoConversion | null;
  /** Background behind the photo (transparent PNGs); null for none. */
  background: string | null;
}

/** Draw the image centre-cropped inside a circle of the given diameter. */
export const drawCircularImage = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  cx: number,
  cy: number,
  diameter: number,
  options: CircularImageOptions
): void => {
  const raster = rasteriseCover(image, diameter, diameter, options.mono);
  if (!raster) return;
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, diameter / 2, 0, Math.PI * 2);
  ctx.clip();
  if (options.background) {
    ctx.fillStyle = options.background;
    ctx.fillRect(cx - diameter / 2, cy - diameter / 2, diameter, diameter);
  }
  ctx.drawImage(
    raster,
    cx - diameter / 2,
    cy - diameter / 2,
    diameter,
    diameter
  );
  ctx.restore();
};

export interface MonogramOptions {
  initials: string;
  fill: string;
  stroke: string | null;
  strokeWidth: number;
  textColor: string;
  fontSize: number;
}

/** Initials in a circle, used in place of a missing photo so the layout never shifts. */
export const drawMonogram = (
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  diameter: number,
  options: MonogramOptions
): void => {
  ctx.save();
  ctx.beginPath();
  ctx.arc(cx, cy, diameter / 2, 0, Math.PI * 2);
  ctx.fillStyle = options.fill;
  ctx.fill();
  if (options.stroke) {
    ctx.strokeStyle = options.stroke;
    ctx.lineWidth = options.strokeWidth;
    ctx.beginPath();
    ctx.arc(cx, cy, diameter / 2 - options.strokeWidth / 2, 0, Math.PI * 2);
    ctx.stroke();
  }
  ctx.fillStyle = options.textColor;
  ctx.font = fontString(700, options.fontSize, DISPLAY_FONT_FAMILY);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.letterSpacing = "0px";
  ctx.fillText(options.initials, cx, cy + options.fontSize * 0.05);
  ctx.restore();
};

export interface ContainedImageOptions {
  align: HorizontalAlign;
  mono: MonoConversion | null;
}

/** Draw the image "object-fit: contain" inside a box, anchored left or centred. Returns the drawn width. */
export const drawContainedImage = (
  ctx: CanvasRenderingContext2D,
  image: HTMLImageElement,
  x: number,
  y: number,
  maxWidth: number,
  height: number,
  options: ContainedImageOptions
): number => {
  const scale = Math.min(
    maxWidth / image.naturalWidth,
    height / image.naturalHeight
  );
  const drawW = image.naturalWidth * scale;
  const drawH = image.naturalHeight * scale;
  const drawX = options.align === "center" ? x + (maxWidth - drawW) / 2 : x;
  const drawY = y + (height - drawH) / 2;
  const source = options.mono
    ? rasteriseCover(image, drawW, drawH, options.mono)
    : image;
  if (!source) return 0;
  ctx.drawImage(source, drawX, drawY, drawW, drawH);
  return drawW;
};

/** The Tactic logo lockup, scaled to `height` and anchored like `drawContainedImage`. */
export const drawTacticLockup = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  maxWidth: number,
  height: number,
  color: string,
  align: HorizontalAlign
): void => {
  const scale = Math.min(
    height / TACTIC_LOCKUP_VIEWBOX.height,
    maxWidth / TACTIC_LOCKUP_VIEWBOX.width
  );
  const drawW = TACTIC_LOCKUP_VIEWBOX.width * scale;
  const drawH = TACTIC_LOCKUP_VIEWBOX.height * scale;
  const drawX = align === "center" ? x + (maxWidth - drawW) / 2 : x;
  ctx.save();
  ctx.translate(drawX, y + (height - drawH) / 2);
  ctx.scale(scale, scale);
  ctx.fillStyle = color;
  for (const path of TACTIC_LOCKUP_PATHS) {
    ctx.fill(new Path2D(path));
  }
  ctx.restore();
};

export interface PillOptions {
  fontSize: number;
  color: string;
  borderWidth: number;
  paddingX: number;
  paddingTop: number;
  paddingBottom: number;
  align: HorizontalAlign;
  /** Width of the area the pill is aligned within. */
  areaWidth: number;
}

export const measurePillHeight = (options: PillOptions): number =>
  options.borderWidth * 2 +
  options.paddingTop +
  options.paddingBottom +
  options.fontSize * 1.2;

/** Outlined, fully rounded label with upper-case tracked text. */
export const drawPill = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: PillOptions
): void => {
  const label = text.toUpperCase();
  ctx.save();
  ctx.font = fontString(600, options.fontSize, BODY_FONT_FAMILY);
  ctx.letterSpacing = `${options.fontSize * 0.05}px`;
  const textWidth = ctx.measureText(label).width;
  const width = textWidth + options.paddingX * 2 + options.borderWidth * 2;
  const height = measurePillHeight(options);
  const pillX =
    options.align === "center" ? x + (options.areaWidth - width) / 2 : x;

  ctx.strokeStyle = options.color;
  ctx.lineWidth = options.borderWidth;
  ctx.beginPath();
  ctx.roundRect(
    pillX + options.borderWidth / 2,
    y + options.borderWidth / 2,
    width - options.borderWidth,
    height - options.borderWidth,
    height / 2
  );
  ctx.stroke();

  ctx.fillStyle = options.color;
  ctx.textAlign = "left";
  ctx.textBaseline = "middle";
  const textY =
    y + options.borderWidth + options.paddingTop + (options.fontSize * 1.2) / 2;
  ctx.fillText(label, pillX + options.borderWidth + options.paddingX, textY);
  ctx.restore();
};

export interface TextLineOptions {
  font: string;
  color: string;
  letterSpacing?: number;
  align: HorizontalAlign;
  areaWidth: number;
}

/** Draw one line of text with its top edge at `y`. */
export const drawTextLine = (
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  options: TextLineOptions
): void => {
  ctx.save();
  ctx.font = options.font;
  ctx.fillStyle = options.color;
  ctx.letterSpacing = `${options.letterSpacing ?? 0}px`;
  ctx.textBaseline = "top";
  ctx.textAlign = options.align === "center" ? "center" : "left";
  const drawX = options.align === "center" ? x + options.areaWidth / 2 : x;
  ctx.fillText(text, drawX, y);
  ctx.restore();
};
